'use client';

import * as React from 'react';
import { cm } from 'src/utils/classNames';

interface TableProps extends React.ComponentProps<'table'> {
	containerRef?: React.RefObject<HTMLDivElement | null>;
	dataTestId?: string;
}

function Table({
	className,
	style,
	containerRef,
	dataTestId,
	...props
}: TableProps) {
	return (
		<div
			ref={containerRef}
			data-slot="table-container"
			className="relative thin-scroll h-full w-full overflow-auto"
		>
			<table
				data-slot="table"
				data-testid={dataTestId}
				className={cm('text-text-default', className)}
				style={style}
				{...props}
			/>
		</div>
	);
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
	return (
		<thead
			data-slot="table-header"
			className={cm(
				'sticky top-0 z-20 rounded-tl-sm rounded-tr-sm border-b border-stroke-divider bg-container-bg',
				className
			)}
			{...props}
		/>
	);
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
	return (
		<tbody
			data-slot="table-body"
			className={cm('[&_tr:last-child]:border-0', className)}
			{...props}
		/>
	);
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
	return (
		<tfoot
			data-slot="table-footer"
			className={cm(
				'border-t border-stroke-divider bg-container-bg',
				className
			)}
			{...props}
		/>
	);
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
	return (
		<tr
			data-slot="table-row"
			className={cm(
				'group bg-container-bg hover:bg-button-disabled',
				className
			)}
			{...props}
		/>
	);
}

function TableHeadRow({ className, ...props }: React.ComponentProps<'tr'>) {
	return (
		<tr
			data-slot="table-head-row"
			className={cm('bg-container-bg', className)}
			{...props}
		/>
	);
}

function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
	return (
		<th
			data-slot="table-head"
			className={cm(
				'py-sm pr-md text-left align-middle typo-b4 whitespace-nowrap text-text-secondary first:pl-md',
				className
			)}
			{...props}
		/>
	);
}

function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
	return (
		<td
			data-slot="table-cell"
			className={cm(
				'h-13 py-sm pr-md align-middle typo-t4 text-text-default first:pl-md',
				className
			)}
			{...props}
		/>
	);
}

export {
	Table,
	TableHeader,
	TableBody,
	TableFooter,
	TableHead,
	TableRow,
	TableHeadRow,
	TableCell,
};
